// API Models

import * as moment from 'moment';
import { FUTURE_VALID_PROVIDER_VISIT_THRESHOLD_DAYS } from '../constants';
import { Address, mapAddress } from './address.model';
import { mapName, Name } from './common.model';
import { ReorderDetails } from './reorder.model';

export interface ApiProviderResponse {
  doctorId: number;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  sanctioned?: boolean;
  pecosRegistered?: boolean;
}

export interface ApiProviderVisitVerificationRequest {
  patientId: number;
  supplies: { itemId: number }[];
}

export interface ApiProviderVisitVerificationResponse {
  visitVerificationRequired: boolean;
  visitVerificationDaysBefore: number;
  visitVerificationDaysAfter: number;
}

export interface ApiProviderVisitsRequest {
  patientId: number;
  doctors: { doctorId: number }[];
  dateOfService: string;
  supplies: { itemId: number }[];
  shipmentType: number;
}

export interface ApiProviderVisitResponse {
  doctorId: number;
  visitDate: string; // date-time string?
}

export interface ApiProviderVisitValidationRequest {
  patientId: number;
  doctorId?: number;
  insuranceId: number;
  visitDate?: string;
  dateOfService: string;
  shipmentType: number;
  supplies: { itemId: number }[];
}

export enum ApiProviderVisitValidationResult {
  Exception = -1,
  VisitDateTooOldCgm = 0,
  VisitDateTooFarInFuture = 1,
  PvvIsValid = 2,
  PvvNotRequired = 3,
  VisitDateTooOldSets = 4,
}

export interface ApiProviderVisitValidationResponse {
  validationResult: ApiProviderVisitValidationResult;
}

// Front End Models

export interface Provider extends Name {
  id: number;
  phoneNumber?: string;
  address?: Address;
  sanctioned?: boolean;
  pecosRegistered?: boolean;
}

export interface ProviderVisitVerification {
  required: boolean;
  daysBefore: number;
  daysAfter: number;
}

export interface ProviderVisit {
  doctorId: number;
  visitDate: Date;
}

// Form Models

export interface NewProviderFormValues {
  providerFirstName: string | null;
  providerLastName: string | null;
  npi: string | null;
  phoneNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
}
export interface ProviderVisitFormValues {
  lastProviderVisit: Date | null;
  hasUpcomingVisit: boolean | null;
  nextProviderVisit: Date | null;
}

// Mappers

export const mapProvider = (p: ApiProviderResponse): Provider => {
  return {
    id: p.doctorId,
    ...mapName(p.firstName, p.lastName),
    phoneNumber: p.phoneNumber,
    address: mapAddress(null, p.addressLine1, p.addressLine2, p.addressCity, p.addressState, p.addressZip),
    sanctioned: p.sanctioned,
    pecosRegistered: p.pecosRegistered,
  };
};

export const mapProviderVisitVerification = (p: ApiProviderVisitVerificationResponse): ProviderVisitVerification => ({
  required: p.visitVerificationRequired,
  daysBefore: p.visitVerificationDaysBefore,
  daysAfter: p.visitVerificationDaysAfter,
});

export const mapProviderVisit = (p: ApiProviderVisitResponse): ProviderVisit => ({
  doctorId: p.doctorId,
  visitDate: new Date(p.visitDate),
});

export const mapProviderFormValues = (p: NewProviderFormValues): Provider => ({
  id: -1,
  ...mapName(p.providerFirstName, p.providerLastName),
  phoneNumber: p.phoneNumber || undefined,
  address: mapAddress(null, p.addressLine1, p.addressLine2, p.addressCity, p.addressState, p.addressZip),
});

export const buildProviderVisitValidationRequest = (
  patientId: number,
  selectedProviderId: number | null,
  primaryInsuranceId: number | null,
  visitDate: string,
  initialTargetShipDate: string,
  reorder: ReorderDetails
): ApiProviderVisitValidationRequest => {
  return {
    patientId,
    doctorId: selectedProviderId || undefined,
    insuranceId: primaryInsuranceId || 0,
    visitDate: moment(visitDate).startOf('day').format('YYYY-MM-DD'),
    dateOfService: moment(initialTargetShipDate).startOf('day').format('YYYY-MM-DD'),
    shipmentType: reorder.shipmentTypeId!,
    supplies: reorder.items.map((i) => ({ itemId: i.id })),
  };
};

export const mapProviderVisitValidationResponse = (
  pvvr: ApiProviderVisitValidationResponse,
  visitDate: string,
  initialTargetShipDate: string
): boolean | null => {
  switch (pvvr.validationResult) {
    case ApiProviderVisitValidationResult.Exception:
      return null;
    case ApiProviderVisitValidationResult.PvvNotRequired:
      return true;
    case ApiProviderVisitValidationResult.VisitDateTooOldCgm:
    case ApiProviderVisitValidationResult.VisitDateTooOldSets:
      return false;
    case ApiProviderVisitValidationResult.VisitDateTooFarInFuture:
    case ApiProviderVisitValidationResult.PvvIsValid:
      // Following iPIMS; they are evaluating future dates in the front end pretty much
      return (
        moment(visitDate).isSameOrBefore(moment(initialTargetShipDate), 'days') ||
        moment(visitDate).isSameOrBefore(moment().add(FUTURE_VALID_PROVIDER_VISIT_THRESHOLD_DAYS, 'days'), 'days')
      );
  }
};
